/**
 * Procedural CHR (pattern-table + nametable + attribute) synthesis.
 *
 * The goal is to produce screens that look "tile-like" rather than pure noise
 * so that glitches read as real-game corruption. We provide a few motif modes
 * that fill both pattern tables, a nametable, and an attribute table from a
 * single seed.
 */

import {
    NAMETABLE_TILES_X,
    NAMETABLE_TILES_Y,
    NAMETABLE_TILES,
    PATTERN_TABLE_BYTES,
    ATTRIBUTE_TABLE_BYTES,
} from "./nesPpu";

export type ChrMotif =
    | "noise"
    | "stripes"
    | "diamonds"
    | "bricks"
    | "circuit";

export interface ChrSynthResult {
    patternTables: [Uint8Array, Uint8Array];
    nametable: Uint8Array;
    attribute: Uint8Array;
}

/** xorshift32 — fast deterministic RNG good enough for tile noise. */
export function makeRng(seed: number): () => number {
    let s = (seed | 0) || 0xDEADBEEF;
    return () => {
        s ^= s << 13;
        s ^= s >>> 17;
        s ^= s << 5;
        return (s >>> 0) / 0x1_0000_0000;
    };
}

function rngInt(rng: () => number, max: number): number {
    return Math.floor(rng() * max);
}

/** Write the 2-bit color value of pixel (px, py) into tile `tileIndex`. */
function writeTilePixel(
    pattern: Uint8Array,
    tileIndex: number,
    px: number,
    py: number,
    value: number,
): void {
    const base = (tileIndex & 0xFF) * 16;
    const bit = 7 - px;
    const lo = pattern[base + py];
    const hi = pattern[base + 8 + py];
    pattern[base + py] = (value & 1) ? lo | (1 << bit) : lo & ~(1 << bit);
    pattern[base + 8 + py] = (value & 2) ? hi | (1 << bit) : hi & ~(1 << bit);
}

/** Generate one 8×8 tile by evaluating `valueAt(px, py)` for each pixel. */
function buildTile(
    pattern: Uint8Array,
    tileIndex: number,
    valueAt: (px: number, py: number) => number,
): void {
    for (let py = 0; py < 8; py++) {
        for (let px = 0; px < 8; px++) {
            writeTilePixel(pattern, tileIndex, px, py, valueAt(px, py) & 3);
        }
    }
}

/** Seeded synthesis of two pattern tables filled with the chosen motif. */
export function synthesizePatternTables(
    seed: number,
    motif: ChrMotif,
): [Uint8Array, Uint8Array] {
    const tables: [Uint8Array, Uint8Array] = [
        new Uint8Array(PATTERN_TABLE_BYTES),
        new Uint8Array(PATTERN_TABLE_BYTES),
    ];
    const rng = makeRng(seed);
    for (const table of tables) {
        for (let t = 0; t < 256; t++) {
            const params = motifParams(rng);
            buildTile(table, t, (px, py) => motifValue(motif, params, px, py));
        }
    }
    return tables;
}

interface MotifParams {
    a: number;
    b: number;
    c: number;
    d: number;
}

function motifParams(rng: () => number): MotifParams {
    return {
        a: rng(),
        b: rng(),
        c: rng(),
        d: rng(),
    };
}

function motifValue(
    motif: ChrMotif,
    p: MotifParams,
    px: number,
    py: number,
): number {
    switch (motif) {
        case "noise": {
            const seed = (px * 374761393 + py * 668265263 + Math.floor(p.a * 0x7FFFFFFF)) | 0;
            const h = ((seed ^ (seed >>> 13)) * 1274126177) >>> 0;
            return h & 3;
        }
        case "stripes": {
            const dir = p.a < 0.33 ? 0 : p.a < 0.66 ? 1 : 2;
            const phase = Math.floor(p.b * 8);
            const period = 1 + Math.floor(p.c * 4);
            const v =
                dir === 0
                    ? Math.floor((px + phase) / period)
                    : dir === 1
                        ? Math.floor((py + phase) / period)
                        : Math.floor((px + py + phase) / period);
            return ((v + (p.d > 0.5 ? 1 : 0)) & 3) === 0 ? 0 : ((v % 3) + 1);
        }
        case "diamonds": {
            const cx = 3.5 + (p.a - 0.5) * 4;
            const cy = 3.5 + (p.b - 0.5) * 4;
            const d = Math.abs(px - cx) + Math.abs(py - cy);
            const r = 1.5 + p.c * 3;
            if (d < r * 0.55) return 1;
            if (d < r * 0.8) return 2;
            if (d < r) return 3;
            return 0;
        }
        case "bricks": {
            const rowH = 3 + Math.floor(p.a * 3);
            const offset = (Math.floor(py / rowH) & 1) === 0 ? 0 : 4;
            const cellW = 3 + Math.floor(p.b * 3);
            const xx = (px + offset) % cellW;
            const onBorder = py % rowH === 0 || xx === 0;
            if (onBorder) return 1;
            const fill = ((Math.floor(py / rowH) + Math.floor((px + offset) / cellW)) & 1) === 0 ? 2 : 3;
            return fill;
        }
        case "circuit": {
            const horiz = (py === 3 || py === 4) && p.a > 0.3;
            const vert = (px === 3 || px === 4) && p.b > 0.3;
            const corner = (px === 3 || px === 4) && (py === 3 || py === 4);
            if (corner) return 3;
            if (horiz || vert) return 2;
            const dot = (px + py) % 4 === 0 && p.c > 0.6;
            return dot ? 1 : 0;
        }
    }
}

/** Random nametable + attribute table using small 16×16 "stamps". */
export function synthesizeLayout(seed: number): {
    nametable: Uint8Array;
    attribute: Uint8Array;
} {
    const nt = new Uint8Array(NAMETABLE_TILES);
    const attr = new Uint8Array(ATTRIBUTE_TABLE_BYTES);
    const rng = makeRng(seed ^ 0x9E3779B9);

    for (let i = 0; i < NAMETABLE_TILES; i++) {
        nt[i] = rngInt(rng, 256);
    }

    // Nudge it toward looking layout-ish: fill horizontal bands with the same
    // run of 8 tiles so the screen has visible rows of repeating motifs.
    for (let row = 0; row < NAMETABLE_TILES_Y; row += 2) {
        if (rng() < 0.5) continue;
        const start = rngInt(rng, 256);
        const span = 4 + rngInt(rng, 8);
        for (let col = 0; col < NAMETABLE_TILES_X; col++) {
            nt[row * NAMETABLE_TILES_X + col] = (start + (col % span)) & 0xFF;
        }
    }

    for (let i = 0; i < ATTRIBUTE_TABLE_BYTES; i++) {
        let byte = 0;
        for (let q = 0; q < 4; q++) {
            byte |= (rngInt(rng, 4) & 3) << (q * 2);
        }
        attr[i] = byte;
    }
    return { nametable: nt, attribute: attr };
}

/** One-shot synthesizer that builds everything from a single seed. */
export function synthesizeChr(seed: number, motif: ChrMotif): ChrSynthResult {
    const patternTables = synthesizePatternTables(seed, motif);
    const { nametable, attribute } = synthesizeLayout(seed);
    return { patternTables, nametable, attribute };
}
